import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Truck, Save, Check, X, CheckCheck, Navigation, TruckIcon, Plus, Trash2, Upload } from "lucide-react";
import { api, photoUrl, uploadFiles, formatApiError } from "../api";
import { TOW_STATUS, fmtDate, fmtDistance, fmtKm, fmtTry } from "../lib/labels";
import BusinessExtras from "../components/BusinessExtras";
import QuoteInbox from "../components/QuoteInbox";

const inputCls = "w-full px-4 py-2.5 rounded-xl bg-slate-950/70 border border-white/10 text-slate-100 text-sm focus:outline-none focus:border-cyan-400/60 transition-colors";
const labelCls = "block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5";
const EMPTY_RECORD = { vin: "", date: new Date().toISOString().slice(0, 10), title: "", description: "", km: "", cost: "", photos: [] };
const SERVICE_RECORD_TYPES = ["Yağ bakımı", "Cam filmi", "PPF", "Detailing", "Lastik hizmeti", "Mekanik bakım", "Ekspertiz", "Diğer hizmet"];

function RequestCard({ r, actions }) {
  const st = TOW_STATUS[r.status] || TOW_STATUS.bekliyor;
  return (
    <div data-testid={`tow-panel-request-${r.id}`} className="rounded-xl border border-white/10 bg-slate-950/50 px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-sm text-slate-100">{[r.brand, r.model].filter(Boolean).join(" ") || "Araç"}{r.plate && ` — ${r.plate}`}{r.tur && <span className="ml-2 text-xs text-orange-300">({r.tur})</span>}</p>
          <p className="text-xs text-slate-500 mt-1">{fmtDate(r.created_at)} • {r.city} • {r.address}</p>
          {r.mesafe_km != null && <p className="text-xs text-cyan-300 mt-1 font-semibold">{fmtDistance(r.mesafe_km)}</p>}
          {r.vin && <p className="text-xs font-mono-vin text-cyan-300 mt-1">{r.vin}</p>}
          <p className="text-xs text-slate-400 mt-1">Talep eden: {r.user_name || r.user_email}</p>
          {r.note && <p className="text-xs text-slate-400 mt-1">Not: {r.note}</p>}
          {r.photos && r.photos.length > 0 && (
            <div className="flex gap-2 mt-2 flex-wrap">
              {r.photos.map((p) => <img key={p} src={photoUrl(p)} alt="" className="w-14 h-10 object-cover rounded-lg border border-white/10" />)}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`text-xs px-3 py-1.5 rounded-full border font-semibold ${st.cls}`}>{st.label}</span>
          {actions}
        </div>
      </div>
    </div>
  );
}

export default function CekiciPanel() {
  const [profile, setProfile] = useState(null);
  const [inbox, setInbox] = useState(null);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [records, setRecords] = useState([]);
  const [rec, setRec] = useState({ ...EMPTY_RECORD });
  const [vinInfo, setVinInfo] = useState(null);
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const PROFILE_DEFAULTS = { company_name: "", driver_name: "", phone: "", city: "", district: "",
    available: false, note: "", lat: 0, lng: 0, service_types: [], regions: [], working_days: [],
    working_hours: "", is_247: false, whatsapp: "", website: "", logo: "", photos: [],
    tax_number: "", tax_document: "", authorized_person: "", status: "bekliyor", verified: false, business_type: "" };

  const load = () => {
    api.get("/cekici/profile").then((r) => setProfile({ ...PROFILE_DEFAULTS, ...r.data }))
      .catch(() => setProfile({ ...PROFILE_DEFAULTS }));
    api.get("/isletme-turleri").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/cekici/talep-listesi").then((r) => setInbox(r.data)).catch(() => setInbox({ gelen: [], aktif: [], tamamlanan: [] }));
    api.get("/servis/records").then((r) => setRecords(r.data)).catch(() => setRecords([]));
  };
  useEffect(() => { load(); }, []);

  const lookupVin = async () => {
    setVinInfo(null);
    if (!rec.vin.trim()) { toast.error("Önce şasi numarası girin"); return; }
    try {
      const { data } = await api.get(`/servis/arac-bul/${encodeURIComponent(rec.vin.trim())}`);
      setVinInfo(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addRecord = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/servis/records", {
        ...rec, km: rec.km === "" ? null : Number(rec.km), cost: rec.cost === "" ? null : Number(rec.cost),
      });
      toast.success("Hizmet kaydı aracın geçmişine eklendi");
      setRec({ ...EMPTY_RECORD });
      setVinInfo(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setAdding(false);
  };

  const removeRecord = async (rid) => {
    if (!window.confirm("Bu hizmet kaydı silinsin mi?")) return;
    try {
      await api.delete(`/servis/records/${rid}`);
      toast.success("Kayıt silindi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const svcOptions = (catalog.find((c) => c.key === profile?.business_type)?.services)
    || (catalog.find((c) => c.key === "cekici")?.services) || [];
  const typeLabel = catalog.find((c) => c.key === profile?.business_type)?.label || "Çekici / Yol Yardım";

  const toggleService = (t) => setProfile((p) => ({
    ...p,
    service_types: (p.service_types || []).includes(t)
      ? p.service_types.filter((x) => x !== t) : [...(p.service_types || []), t],
  }));

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/cekici/profile", profile);
      toast.success("Çekici profili kaydedildi");
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const act = async (tid, action) => {
    try {
      await api.post(`/cekici/talep/${tid}/${action}`);
      toast.success(action === "kabul" ? "Talep kabul edildi" : action === "red" ? "Talep reddedildi" : "İş tamamlandı");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (profile === null || inbox === null) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="w-10 h-10 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="cekici-panel" className="min-h-screen pt-20 sm:pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="flex items-center gap-3">
          <Truck className="w-6 h-6 text-orange-300" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Çekici Paneli</h1>
        </div>

        {profile.status && profile.status !== "onayli" && (
          <div data-testid="tow-status-banner" className={`rounded-xl border px-4 py-3 text-sm ${
            profile.status === "bekliyor" ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
            : "border-red-500/30 bg-red-500/10 text-red-300"}`}>
            {profile.status === "bekliyor"
              ? "Başvurunuz yönetici doğrulaması bekliyor. Onaylandığında bölgenizdeki talepleri görmeye başlarsınız."
              : profile.status === "reddedildi"
                ? "Başvurunuz reddedildi. Bilgilerinizi güncelleyip tekrar değerlendirme talep edebilirsiniz."
                : "Hesabınız pasif durumda. Talep almak için yöneticiyle iletişime geçin."}
          </div>
        )}

        {/* Profil + müsaitlik */}
        <form onSubmit={saveProfile} data-testid="tow-profile-card"
          className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl p-5 sm:p-7 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-lg font-semibold text-slate-100">Firma Profili & Müsaitlik</h2>
            <label data-testid="tow-available-toggle" className="flex items-center gap-2.5 cursor-pointer">
              <span className="text-sm text-slate-300">Müsaitim</span>
              <button type="button" onClick={() => setProfile((p) => ({ ...p, available: !p.available }))}
                className={`w-12 h-6 rounded-full transition-colors relative ${profile.available ? "bg-emerald-500" : "bg-slate-700"}`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${profile.available ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Firma Adı *</label>
              <input data-testid="tow-company-name" required value={profile.company_name}
                onChange={(e) => setProfile({ ...profile, company_name: e.target.value })} className={inputCls} placeholder="Yılmaz Oto Kurtarma" /></div>
            <div><label className={labelCls}>Şoför Adı</label>
              <input data-testid="tow-driver-name" value={profile.driver_name}
                onChange={(e) => setProfile({ ...profile, driver_name: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Telefon *</label>
              <input data-testid="tow-phone" required value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className={inputCls} placeholder="0 5xx xxx xx xx" /></div>
            <div><label className={labelCls}>Şehir (Çalışma Bölgesi) *</label>
              <input data-testid="tow-city" required value={profile.city}
                onChange={(e) => setProfile({ ...profile, city: e.target.value })} className={inputCls} placeholder="İstanbul" /></div>
            <div><label className={labelCls}>İlçe / Bölge</label>
              <input data-testid="tow-district" value={profile.district}
                onChange={(e) => setProfile({ ...profile, district: e.target.value })} className={inputCls} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Not</label>
              <input data-testid="tow-note" value={profile.note}
                onChange={(e) => setProfile({ ...profile, note: e.target.value })} className={inputCls} placeholder="7/24 hizmet..." /></div>
            <div className="flex items-end">
              <button type="button" data-testid="tow-use-location"
                onClick={() => {
                  if (!navigator.geolocation) { toast.error("Tarayıcınız konum desteklemiyor."); return; }
                  navigator.geolocation.getCurrentPosition(
                    (p) => { setProfile((pr) => ({ ...pr, lat: p.coords.latitude, lng: p.coords.longitude })); toast.success("Konum alındı — kaydetmeyi unutmayın"); },
                    () => toast.error("Konum izni verilmedi."), { timeout: 10000 });
                }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-cyan-400/40 text-cyan-300 text-sm font-semibold hover:bg-cyan-400/10 transition-colors w-full justify-center">
                <Navigation className="w-4 h-4" /> {profile.lat ? "Konum ✓" : "Konumumu Al"}
              </button>
            </div>
          </div>{svcOptions.length > 0 && (
            <div>
              <label className={labelCls}>Verilen Hizmetler</label>
              <div className="flex flex-wrap gap-2">
                {svcOptions.map((t) => (
                  <button key={t} type="button" data-testid={`tow-service-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => toggleService(t)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      (profile.service_types || []).includes(t)
                        ? "border-orange-400/60 bg-orange-500/15 text-orange-300"
                        : "border-white/10 text-slate-400 hover:text-slate-200"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}
          <BusinessExtras profile={profile} setProfile={setProfile} typeLabel={typeLabel} />
          <button data-testid="tow-profile-save" type="submit" disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-cyan-400 text-slate-950 font-bold text-sm hover:bg-cyan-300 transition-all disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? "Kaydediliyor..." : "Profili Kaydet"}
          </button>
          <p className="text-xs text-slate-500">Müsaitlik açıkken ve şehrinizdeki talepler aşağıda listelenir.</p>
        </form>

        {/* Gelen talepler */}
        <div data-testid="tow-inbox" className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl p-5 sm:p-7">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">Gelen Talepler ({inbox.gelen.length})</h2>
          {inbox.gelen.length === 0 ? (
            <p className="text-sm text-slate-500">Şehrinizde bekleyen talep yok.</p>
          ) : (
            <div className="space-y-3">
              {inbox.gelen.map((r) => (
                <RequestCard key={r.id} r={r} actions={
                  <div className="flex gap-2">
                    <button data-testid={`tow-accept-${r.id}`} onClick={() => act(r.id, "kabul")}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs font-bold hover:bg-emerald-500/20 transition-colors">
                      <Check className="w-3.5 h-3.5" /> Kabul Et
                    </button>
                    <button data-testid={`tow-reject-${r.id}`} onClick={() => act(r.id, "red")}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-white/10 text-slate-300 text-xs hover:border-red-400/50 hover:text-red-300 transition-colors">
                      <X className="w-3.5 h-3.5" /> Reddet
                    </button>
                  </div>
                } />
              ))}
            </div>
          )}
        </div>

        {/* Aktif işler */}
        <div data-testid="tow-active" className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl p-5 sm:p-7">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">Aktif İşlerim ({inbox.aktif.length})</h2>
          {inbox.aktif.length === 0 ? (
            <p className="text-sm text-slate-500">Aktif iş yok.</p>
          ) : (
            <div className="space-y-3">
              {inbox.aktif.map((r) => (
                <RequestCard key={r.id} r={r} actions={
                  <div className="flex gap-2">
                    {r.status === "kabul_edildi" && (
                      <button data-testid={`tow-enroute-${r.id}`} onClick={() => act(r.id, "yolda")}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-500/10 border border-violet-500/40 text-violet-300 text-xs font-bold hover:bg-violet-500/20 transition-colors">
                        <TruckIcon className="w-3.5 h-3.5" /> Yoldayım
                      </button>
                    )}
                    <button data-testid={`tow-complete-${r.id}`} onClick={() => act(r.id, "tamamla")}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-400/10 border border-cyan-400/40 text-cyan-300 text-xs font-bold hover:bg-cyan-400/20 transition-colors">
                      <CheckCheck className="w-3.5 h-3.5" /> İşi Tamamla
                    </button>
                  </div>
                } />
              ))}
            </div>
          )}
        </div>

        {/* VIN ile hizmet kaydı */}
        <form onSubmit={addRecord} data-testid="tow-record-card"
          className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl p-5 sm:p-7 space-y-4">
          <h2 className="text-lg font-semibold text-slate-100">VIN / Şasi ile Hizmet Kaydı Ekle</h2>
          <p className="text-xs text-slate-500">Kayıt, aracın OTO KÜNYE geçmişine işlenir. Araç sahibinin diğer bilgileri gösterilmez.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><label className={labelCls}>Şasi No / VIN *</label>
              <div className="flex gap-2">
                <input data-testid="tow-record-vin" required value={rec.vin}
                  onChange={(e) => { setRec({ ...rec, vin: e.target.value.toUpperCase() }); setVinInfo(null); }}
                  className={`${inputCls} font-mono-vin tracking-widest flex-1`} placeholder="17 haneli şasi numarası" />
                <button type="button" data-testid="tow-record-lookup" onClick={lookupVin}
                  className="px-4 py-2.5 rounded-xl border border-cyan-400/40 text-cyan-300 text-sm font-semibold hover:bg-cyan-400/10 transition-colors shrink-0">
                  Aracı Bul
                </button>
              </div>
              {vinInfo && (
                <p data-testid="tow-record-vehicle-info" className="mt-2 text-xs font-semibold text-emerald-300">
                  {vinInfo.brand} {vinInfo.model}{vinInfo.year ? ` • ${vinInfo.year}` : ""}{vinInfo.body_type ? ` • ${vinInfo.body_type}` : ""}
                </p>
              )}</div>
            <div><label className={labelCls}>Tarih *</label>
              <input data-testid="tow-record-date" required type="date" value={rec.date}
                onChange={(e) => setRec({ ...rec, date: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Yapılan İşlem *</label>
              <select data-testid="tow-record-title" required value={rec.title}
                onChange={(e) => setRec({ ...rec, title: e.target.value })} className={inputCls}>
                <option value="">İşlem seçin...</option>
                {SERVICE_RECORD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><label className={labelCls}>Kilometre</label>
              <input data-testid="tow-record-km" type="number" min="0" value={rec.km}
                onChange={(e) => setRec({ ...rec, km: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Tutar (₺)</label>
              <input data-testid="tow-record-cost" type="number" min="0" value={rec.cost}
                onChange={(e) => setRec({ ...rec, cost: e.target.value })} className={inputCls} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Açıklama</label>
              <textarea data-testid="tow-record-description" rows={2} value={rec.description}
                onChange={(e) => setRec({ ...rec, description: e.target.value })} className={inputCls} /></div>
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
              onChange={async (e) => {
                setUploading(true);
                try {
                  const paths = await uploadFiles([...e.target.files]);
                  setRec((r) => ({ ...r, photos: [...r.photos, ...paths] }));
                } catch (err) { toast.error(formatApiError(err)); }
                setUploading(false);
                e.target.value = "";
              }} />
            <button type="button" data-testid="tow-record-upload" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-white/20 text-slate-300 text-sm hover:border-cyan-400/50 hover:text-cyan-300 transition-colors disabled:opacity-50">
              <Upload className="w-4 h-4" /> {uploading ? "Yükleniyor..." : "Belge / Fotoğraf Ekle"}
            </button>
            {rec.photos.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {rec.photos.map((p, i) => (
                  <div key={p} className="relative">
                    <img src={photoUrl(p)} alt="" className="w-16 h-12 object-cover rounded-lg border border-white/10" />
                    <button type="button" onClick={() => setRec((r) => ({ ...r, photos: r.photos.filter((_, j) => j !== i) }))}
                      className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button data-testid="tow-record-submit" type="submit" disabled={adding || uploading}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-emerald-500 text-slate-950 font-bold text-sm hover:bg-emerald-400 transition-all disabled:opacity-50">
            <Plus className="w-4 h-4" /> {adding ? "Ekleniyor..." : "Kaydı Araca İşle"}
          </button>
        </form>

        {/* Tamamlananlar */}
        <QuoteInbox />
        <div data-testid="tow-records-card" className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl overflow-hidden">
          <h2 className="text-lg font-semibold text-slate-100 px-5 sm:px-7 pt-5">Eklediğim Hizmet Kayıtları ({records.length})</h2>
          <div className="p-5 sm:p-7 pt-4 overflow-x-auto">
            {records.length === 0 ? (
              <p className="text-sm text-slate-500">Henüz hizmet kaydı eklemediniz.</p>
            ) : (
              <table data-testid="tow-records-table" className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="py-3 pr-4">Tarih</th>
                    <th className="py-3 pr-4">Şasi No</th>
                    <th className="py-3 pr-4">İşlem</th>
                    <th className="py-3 pr-4">KM</th>
                    <th className="py-3 text-right">Sil</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r) => (
                    <tr key={r.record_id} className="border-b border-white/5 hover:bg-slate-800/40 transition-colors">
                      <td className="py-3 pr-4 text-slate-400 whitespace-nowrap">{fmtDate(r.date)}</td>
                      <td className="py-3 pr-4 font-mono-vin text-cyan-300 text-xs">{r.vin}</td>
                      <td className="py-3 pr-4 text-slate-200">{r.title}</td>
                      <td className="py-3 pr-4 text-slate-400">{fmtKm(r.km)}</td>
                      <td className="py-3 text-right">
                        <button data-testid={`tow-record-delete-${r.record_id}`} onClick={() => removeRecord(r.record_id)}
                          className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-red-300 hover:border-red-400/40 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <div data-testid="tow-completed" className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl p-5 sm:p-7">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">Tamamlanan İşler ({inbox.tamamlanan.length})</h2>
          {inbox.tamamlanan.length === 0 ? (
            <p className="text-sm text-slate-500">Tamamlanan iş yok.</p>
          ) : (
            <div className="space-y-3">
              {inbox.tamamlanan.map((r) => <RequestCard key={r.id} r={r} actions={null} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
